import { useState } from "react";
import { FarmaClassLogo } from "@/components/FarmaClassLogo";
import { FARMANALIZA_URL, trackFarmanaliza } from "@/lib/farmanaliza";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OnboardingModal from "@/components/OnboardingModal";
import CurrentStudyBar from "@/components/CurrentStudyBar";
import ErrorBoundary from "@/components/ErrorBoundary";
import AssistantChat from "@/components/AssistantChat";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  UploadCloud,
  PackagePlus,
  History,
  Zap,
  BookOpen,
  LogOut,
  Users as UsersIcon,
  TrendingUp,
  Trophy,
  Sparkles,
  Globe2,
  KeyRound,
  Inbox,
  Download,
  Briefcase,
  UserCog,
  PieChart,
  UserCircle,
  Truck,
  Handshake,
  Gauge,
  LayoutDashboard,
  AlertTriangle,
  Mail,
  BarChart3,
  ExternalLink,
  LineChart as LineChartIcon,
  SlidersHorizontal,
  Lightbulb,
  Menu,
  PackageX,
} from "lucide-react";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isSubscription = isAdmin || user?.license_type?.startsWith("subscription");
  // Marca blanca: Sandoz además oculta condiciones de laboratorio y comparativa
  const BRANDS = {
    sandoz: { label: "Sandoz", logo: "/sandoz-logo.png", width: "w-[150px]", mobile: "w-[110px]" },
    normon: { label: "Normon", logo: "/normon-logo.png", width: "w-[160px]", mobile: "w-[120px]" },
    aurovitas: { label: "Aurovitas", logo: "/aurovitas-logo.png", width: "w-[165px]", mobile: "w-[125px]" },
  };
  const brand = BRANDS[user?.brand] || null;
  // Color de los iconos: el corporativo de la matriz o el turquesa de Archiduque Consulting
  const iconColor =
    { sandoz: "#1F58B0", normon: "#001E62", aurovitas: "#3B3FA3" }[user?.brand] || "#4BB7D1";
  const isSandoz = user?.brand === "sandoz";

  // Tres bloques del menú, con su matiz del color corporativo
  const NAV_GROUPS = {
    study: { label: "Resultado y estudio", tint: iconColor, soft: 1 },
    team: { label: "Personal e incentivos", tint: iconColor, soft: 0.78 },
    supply: { label: "Proveedores y condiciones", tint: iconColor, soft: 0.58 },
    upload: { label: "Subir tus datos", tint: iconColor, soft: 0.9 },
    info: { label: "Descargas y más información", tint: iconColor, soft: 0.45 },
    admin: { label: "Administración", tint: iconColor, soft: 0.32 },
  };

  // Build nav according to role/plan
  const nav = [
    // La home ("/") es el Resumen del estudio (KPIs + comparativa vs mes anterior).
    { to: "/", label: "Resumen del estudio", icon: Gauge, end: true, testid: "nav-resumen", group: "study" },
    { to: "/cuadro-mando", label: "Cuadro de mando", icon: LayoutDashboard, testid: "nav-cuadro", group: "study" },
    { to: "/recomendaciones", label: "Recomendaciones", icon: Lightbulb, testid: "nav-recomendaciones", group: "study" },
    { to: "/history", label: "Historial y estudios", icon: History, testid: "nav-history", group: "study" },
    { to: "/latest-study", label: "Estudio más reciente", icon: Zap, testid: "nav-latest-study", group: "study" },
    { to: "/solo-marca", label: "Moléculas que no tenemos", icon: AlertTriangle, testid: "nav-solo-marca", group: "study" },
    { to: "/stock-parado", label: "Stock que no vendes", icon: PackageX, testid: "nav-stock-parado", group: "study" },
    ...(isSubscription
      ? [
          ...(isSandoz ? [] : [{ to: "/comparison", label: "Comparativa", icon: TrendingUp, testid: "nav-comparison", group: "study" }]),
          { to: "/incentives", label: "Incentivos", icon: Trophy, testid: "nav-incentives", group: "team" },
          { to: "/simulator", label: "Simulador de beneficio", icon: Sparkles, testid: "nav-simulator", group: "team" },
          { to: "/empleados", label: "Personal", icon: UserCog, testid: "nav-empleados", group: "team" },
          { to: "/empleados/analisis", label: "Análisis por empleado", icon: PieChart, testid: "nav-empanalysis", group: "team" },
          { to: "/minimos-maximos", label: "Mínimos y máximos", icon: SlidersHorizontal, testid: "nav-minmax", group: "supply" },
          { to: "/proveedores", label: "Proveedores", icon: Truck, end: true, testid: "nav-proveedores", group: "supply" },
          ...(isSandoz
            ? [{ to: "/proveedores/preferentes", label: "Mis laboratorios", icon: Handshake, testid: "nav-preferentes", group: "supply" }]
            : [{ to: "/proveedores/condiciones", label: "Condiciones laboratorios", icon: Handshake, testid: "nav-condiciones", group: "supply" }]),
        ]
      : []),
    { to: "/benchmark", label: "Benchmark provincial", icon: Globe2, testid: "nav-benchmark", group: "supply" },
    // Accesos destacados: los dos ficheros que la farmacia sube cada mes.
    { to: "/upload", label: "Subir dispensación", icon: UploadCloud, testid: "nav-upload", highlight: true, groupStart: true, group: "upload" },
    { to: "/stock", label: "Subir stock", icon: PackagePlus, testid: "nav-stock", highlight: true, group: "upload" },
    { to: "/descargas", label: "Descargas", icon: Download, testid: "nav-descargas", group: "info" },
    { to: "/servicios", label: "Servicios", icon: Briefcase, testid: "nav-servicios", group: "info" },
    { to: "/account", label: "Mi cuenta", icon: UserCircle, testid: "nav-account", group: "info" },
    ...(isAdmin
      ? [
          { to: "/catalog", label: "Catálogo CN", icon: BookOpen, testid: "nav-catalog", group: "admin" },
          { to: "/licenses", label: "Licencias", icon: KeyRound, testid: "nav-licenses", group: "admin" },
          { to: "/leads", label: "Solicitudes", icon: Inbox, testid: "nav-leads", group: "admin" },
          { to: "/users", label: "Usuarios", icon: UsersIcon, testid: "nav-users", group: "admin" },
          { to: "/admin/mejoras", label: "Mejoras Archiduque", icon: LineChartIcon, testid: "nav-admin-mejoras", group: "admin" },
          { to: "/admin/correo-clientes", label: "Correo a clientes", icon: Mail, testid: "nav-admin-campaign", group: "admin" },
          { to: "/admin/errors", label: "Errores JS", icon: AlertTriangle, testid: "nav-admin-errors", group: "admin" },
        ]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const planBadge = (() => {
    if (isAdmin) return { text: "Admin", cls: "bg-amber-100 text-amber-800" };
    if (user?.license_type === "subscription_annual") return { text: "PRO Anual", cls: "bg-slate-900 text-white" };
    if (user?.license_type === "subscription_monthly") return { text: "PRO Mensual", cls: "bg-slate-900 text-white" };
    if (user?.license_type === "single_use") return { text: "Uso único", cls: "bg-slate-200 text-slate-700" };
    return null;
  })();

  const currentPageLabel = (nav.find((n) => n.to === location.pathname)?.label) || "FarmaClass";

  const downloadManual = async () => {
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const resp = await fetch(`${base}/api/manual/download`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "FarmaClass-Manual-Usuario.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {
      window.open(
        `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/manual/download`,
        "_blank",
      );
    }
  };

  // Contenido del sidebar reutilizable (escritorio + drawer móvil)
  const sidebarContent = (onNavigate) => (
    <div className="flex flex-col h-full bg-white">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          {brand ? (
            <img
              src={brand.logo}
              alt={brand.label}
              className={`${brand.width} h-auto object-contain rounded-md`}
              data-testid={`brand-logo-${user.brand}`}
            />
          ) : (
            <FarmaClassLogo className="h-11" testId="sidebar-farmaclass-logo" />
          )}
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {nav.map((item, idx) => {
          const g = item.group ? NAV_GROUPS[item.group] : null;
          const isFirstOfGroup = !!item.group && nav[idx - 1]?.group !== item.group;
          return (
          <div key={item.to} className={item.groupStart ? "pt-3 mt-2 border-t border-slate-200" : ""}>
            {isFirstOfGroup && (
              <div
                className="flex items-center gap-2 px-1.5 pt-4 pb-1.5"
                data-testid={`nav-group-${item.group}`}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: g.tint, opacity: g.soft }}
                />
                <span className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-slate-400">
                  {g.label}
                </span>
                <span className="flex-1 h-px" style={{ backgroundColor: g.tint, opacity: g.soft * 0.28 }} />
              </div>
            )}
            {item.href ? null : (
            <NavLink
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-slate-950 text-white font-bold"
                    : item.highlight
                      ? "font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
              style={g ? { borderLeft: `2px solid ${g.tint}`, borderLeftColor: g.tint } : undefined}
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className="h-4 w-4"
                    strokeWidth={item.highlight ? 2.2 : 1.5}
                    style={isActive ? undefined : { color: g ? g.tint : iconColor, opacity: g ? g.soft : 1 }}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
            )}
          </div>
          );
        })}
      </nav>

      <div className="px-4 pt-3">
        <a
          href={FARMANALIZA_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-farmanaliza"
          onClick={() => { trackFarmanaliza("menu"); onNavigate?.(); }}
          className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-bold text-white bg-emerald-600 shadow-sm shadow-emerald-600/30 hover:bg-emerald-700 hover:-translate-y-px transition-[background-color,transform] duration-200"
        >
          <BarChart3 className="h-5 w-5 shrink-0" strokeWidth={2.2} />
          <span className="flex-1 leading-tight">¿Problemas de stock o ventas?</span>
          <ExternalLink className="h-4 w-4 opacity-80" />
        </a>
      </div>

      <div className="p-4 border-t border-slate-200">
        <div className="px-3 py-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Sesión</div>
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="current-user-email" title={user?.email}>
            {user?.pharmacy_name || user?.email}
          </div>
          {planBadge && (
            <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold ${planBadge.cls}`}>
              {planBadge.text}
            </div>
          )}
        </div>
        <button
          data-testid="sidebar-manual-link"
          onClick={downloadManual}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
          title="Descargar manual de usuario en PDF"
        >
          <BookOpen className="h-4 w-4" strokeWidth={1.5} />
          Manual de usuario
        </button>
        <button
          data-testid="logout-button"
          onClick={() => {
            if (onNavigate) onNavigate();
            handleLogout();
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-600 hover:bg-red-50 hover:text-red-700 transition-all"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen flex bg-slate-50${user?.brand ? ` brand-${user.brand}` : ""}`} data-testid="app-layout">
      {/* Sidebar fijo en escritorio (>= lg) */}
      <aside className="hidden lg:flex w-64 border-r border-slate-200 flex-col sticky top-0 h-screen">
        {sidebarContent()}
      </aside>

      {/* Drawer móvil (< lg) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-drawer">
          {sidebarContent(() => setMobileOpen(false))}
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0">
        {/* Topbar móvil con hamburguesa */}
        <header
          className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 bg-white border-b border-slate-200 px-4 py-3"
          data-testid="mobile-topbar"
        >
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-slate-100 transition-colors"
                data-testid="mobile-menu-button"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" strokeWidth={1.8} />
              </button>
            </SheetTrigger>
          </Sheet>
          <div className="flex items-center gap-2 min-w-0">
            {brand ? (
              <img src={brand.logo} alt={brand.label} className={`${brand.mobile} h-auto object-contain shrink-0 rounded`} />
            ) : (
              <FarmaClassLogo variant="icon" className="h-7 shrink-0" testId="mobile-farmaclass-icon" />
            )}
            <div className="min-w-0">
              {!brand && <div className="font-display text-sm font-bold tracking-tight truncate">FarmaClass</div>}
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 truncate">{currentPageLabel}</div>
            </div>
          </div>
          <div className="w-10" /> {/* spacer for visual balance */}
        </header>

        <CurrentStudyBar />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
        <OnboardingModal />
        <AssistantChat />
      </main>
    </div>
  );
}
